import {$isLinkNode} from '@lexical/link';
import {$findMatchingParent} from '@lexical/utils';
import {$isTextNode, LexicalEditor} from 'lexical';

import {isNil, isNotNil} from 'ramda';

import {TextEditorSelection} from '../../../types/TextEditorApi';
import {$collectEditorNodeRanges} from './collectEditorNodeRanges';
import {$resolveEditorIndexRange} from './resolveEditorIndexRange';
import {$serializeEditorHtml} from './serializeEditorHtml';

export function $serializeEditorRange(
  editor: LexicalEditor,
  selection: TextEditorSelection
): string | null {
  const {rangeByKey, leaves} = $collectEditorNodeRanges();
  const start = selection.index;
  const end = start + selection.length;
  let output = '';
  let segmentStart: number | null = null;
  let isResolvable = true;

  const flushSegment = (segmentEnd: number) => {
    if (isNil(segmentStart) || segmentEnd <= segmentStart) {
      segmentStart = null;
      return;
    }

    const range = $resolveEditorIndexRange({
      index: segmentStart,
      length: segmentEnd - segmentStart,
    });

    if (isNil(range)) {
      isResolvable = false;
    } else {
      output += $serializeEditorHtml(editor, range);
    }

    segmentStart = null;
  };

  leaves.forEach((leaf) => {
    const leafStart = Math.max(leaf.start, start);
    const leafEnd = Math.min(leaf.end, end);

    if (leafEnd <= leafStart) {
      return;
    }

    const link = $findMatchingParent(leaf.node, $isLinkNode);
    const linkRange = isNotNil(link) ? rangeByKey.get(link.getKey()) : undefined;
    const isPartialLink = isNotNil(linkRange) && (linkRange.start < start || linkRange.end > end);

    // The form's text exporter reads the live node, not Lexical's sliced clone.
    if (isPartialLink || ($isTextNode(leaf.node) && isNil(link))) {
      flushSegment(leafStart);
      output += leaf.node.getTextContent().slice(leafStart - leaf.start, leafEnd - leaf.start);
      return;
    }

    if (isNil(segmentStart)) {
      segmentStart = leafStart;
    }
  });

  flushSegment(end);

  return isResolvable ? output : null;
}
